import { useState } from 'react';
import { ExternalLink, KeyRound, Loader2, MoreVertical } from 'lucide-react';
import { toast } from 'sonner@2.0.3';

import { Button } from '../../../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { AccountCard, platformLabel, type Account } from '../../../entities/account';
import { popupLogin, PopupLoginClosed, STAFF_AUTH_URL } from '../../../features/account-add';
import { invalidateTikTokAccounts } from '../../../shared/api/tiktok.api';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';

interface PlatformAccountCardProps {
  /** TikTok или YouTube в общей модели Account. */
  account: Account;
  /** Профиль в самой сети. */
  profileUrl?: string;
  followersLoading?: boolean;
  highlighted?: boolean;
  /** После переподключения — перечитать список. */
  onChanged?: () => void;
}

/**
 * Карточка TikTok-аккаунта или YouTube-канала на странице «Аккаунты».
 *
 * Меню короче, чем у Instagram: удаления и тэгов у этих сетей на бэкенде нет
 * (02.11.2026) — только открыть профиль и, для TikTok, войти заново.
 */
export function PlatformAccountCard({ account, profileUrl, followersLoading, highlighted, onChanged }: PlatformAccountCardProps) {
  const { isAdmin } = useCurrentUser();
  const [reconnecting, setReconnecting] = useState(false);
  const label = platformLabel(account.platform ?? 'tiktok');

  const reconnectTikTok = async () => {
    setReconnecting(true);
    try {
      await popupLogin('tiktok', STAFF_AUTH_URL.tiktok);
      toast.success('TikTok переподключён');
    } catch (err) {
      if (!(err instanceof PopupLoginClosed)) toast.error(err instanceof Error ? err.message : 'Не удалось переподключить TikTok');
    } finally {
      invalidateTikTokAccounts();
      setReconnecting(false);
      onChanged?.();
    }
  };

  const canReconnect = isAdmin && account.platform === 'tiktok';
  const menu = (profileUrl || canReconnect) ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Действия с аккаунтом">
          {reconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {profileUrl && (
          <DropdownMenuItem onClick={() => window.open(profileUrl, '_blank', 'noopener,noreferrer')}>
            <ExternalLink className="h-4 w-4" />
            Открыть в {label}
          </DropdownMenuItem>
        )}
        {canReconnect && (
          <DropdownMenuItem onClick={() => void reconnectTikTok()} disabled={reconnecting}>
            <KeyRound className="h-4 w-4" />
            Переподключить TikTok
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : undefined;

  return (
    <AccountCard
      account={account}
      actions={menu}
      externalUrl={profileUrl}
      followersLoading={followersLoading}
      highlighted={highlighted}
    />
  );
}
